// src/components/config/TrocarSenha.jsx
import React, { useMemo, useState } from "react";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";

import {
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
  sendPasswordResetEmail,
} from "firebase/auth";

// ✅ ajuste correto se seu arquivo está em src/firebaseConfig
import { auth } from "../firebaseConfig";

import { FaEye, FaEyeSlash, FaLock } from "react-icons/fa";

function humanizeAuthError(err) {
  const code = err?.code || "";
  switch (code) {
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Senha atual incorreta.";
    case "auth/weak-password":
      return "Senha fraca. Use pelo menos 8 caracteres (de preferência com letras e números).";
    case "auth/requires-recent-login":
      return "Por segurança, faça login novamente e tente de novo.";
    case "auth/network-request-failed":
      return "Falha de rede. Verifique sua conexão.";
    default:
      return err?.message || "Não foi possível alterar a senha.";
  }
}

const TrocarSenha = () => {
  const [senhaAtual, setSenhaAtual] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [loading, setLoading] = useState(false);

  const [showAtual, setShowAtual] = useState(false);
  const [showNova, setShowNova] = useState(false);
  const [showConf, setShowConf] = useState(false);

  const user = auth.currentUser;

  const isPasswordProvider = useMemo(() => {
    const providers = user?.providerData?.map((p) => p.providerId) || [];
    return providers.includes("password") || !providers.length;
  }, [user]);

  const validate = () => {
    if (!user || !user.email) throw new Error("Usuário não autenticado.");
    if (!isPasswordProvider) {
      throw new Error("Sua conta não usa senha (login social). Use o provedor para gerenciar a senha.");
    }
    if (!senhaAtual) throw new Error("Informe sua senha atual.");
    if (!novaSenha || novaSenha.length < 8) throw new Error("A nova senha precisa ter pelo menos 8 caracteres.");
    if (novaSenha !== confirmacao) throw new Error("A confirmação não confere.");
    if (novaSenha === senhaAtual) throw new Error("A nova senha não pode ser igual à senha atual.");
  };

  const toggleBtnClass =
    "absolute right-3 top-1/2 -translate-y-1/2 inline-flex h-9 w-9 items-center justify-center " +
    "rounded-lg text-gray-400 hover:text-gestor-blue hover:bg-gestor-blue/5 " +
    "focus:outline-none focus:ring-2 focus:ring-gestor-blue/20 transition";

  const inputClass =
    "w-full p-3 rounded-lg border border-gray-200 focus:border-gestor-blue " +
    "focus:ring-2 focus:ring-gestor-orange/30 bg-white text-gray-900 outline-none transition pr-12";

  const handleTrocarSenha = async (e) => {
    e?.preventDefault?.();
    try {
      validate();

      setLoading(true);

      const cred = EmailAuthProvider.credential(user.email, senhaAtual);
      await reauthenticateWithCredential(user, cred);
      await updatePassword(user, novaSenha);

      setSenhaAtual("");
      setNovaSenha("");
      setConfirmacao("");

      await Swal.fire({
        icon: "success",
        title: "Senha alterada!",
        text: "Sua senha foi atualizada com sucesso.",
        confirmButtonText: "OK",
      });
    } catch (err) {
      const msg = err?.message || humanizeAuthError(err);
      await Swal.fire({
        icon: "error",
        title: "Não foi possível alterar",
        text: msg,
        confirmButtonText: "Entendi",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEsqueciSenha = async () => {
    try {
      if (!user?.email) throw new Error("Usuário não autenticado.");
      await sendPasswordResetEmail(auth, user.email);

      await Swal.fire({
        icon: "info",
        title: "E-mail enviado",
        text: "Enviei um link para redefinição de senha no seu e-mail.",
        confirmButtonText: "OK",
      });
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "Não foi possível enviar",
        text: humanizeAuthError(err),
        confirmButtonText: "Entendi",
      });
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gestor-blue/10 shadow-xl p-6 animate-fade-in">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 inline-flex h-10 w-10 items-center justify-center rounded-xl bg-gestor-blue/5 text-gestor-blue">
            <FaLock />
          </div>

          <div>
            <h2 className="text-lg font-bold text-gray-900">Trocar senha</h2>
            <p className="text-sm text-gray-500 mt-1">
              Por segurança, confirme sua senha atual antes de definir a nova.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleEsqueciSenha}
          className="text-sm font-semibold text-gestor-blue hover:underline"
        >
          Esqueci minha senha
        </button>
      </div>

      <form onSubmit={handleTrocarSenha} className="mt-5 grid gap-3 max-w-xl">
        {/* senha atual */}
        <label className="text-sm font-semibold text-gray-600">Senha atual</label>
        <div className="relative">
          <input
            type={showAtual ? "text" : "password"}
            value={senhaAtual}
            onChange={(e) => setSenhaAtual(e.target.value)}
            className={inputClass}
            autoComplete="current-password"
            placeholder="Digite sua senha atual"
          />
          <button
            type="button"
            onClick={() => setShowAtual((p) => !p)}
            className={toggleBtnClass}
            aria-label={showAtual ? "Ocultar senha atual" : "Mostrar senha atual"}
            title={showAtual ? "Ocultar" : "Mostrar"}
          >
            {showAtual ? <FaEyeSlash /> : <FaEye />}
          </button>
        </div>

        {/* nova senha */}
        <label className="text-sm font-semibold text-gray-600">Nova senha</label>
        <div className="relative">
          <input
            type={showNova ? "text" : "password"}
            value={novaSenha}
            onChange={(e) => setNovaSenha(e.target.value)}
            className={inputClass}
            autoComplete="new-password"
            placeholder="Mínimo 8 caracteres"
          />
          <button
            type="button"
            onClick={() => setShowNova((p) => !p)}
            className={toggleBtnClass}
            aria-label={showNova ? "Ocultar nova senha" : "Mostrar nova senha"}
            title={showNova ? "Ocultar" : "Mostrar"}
          >
            {showNova ? <FaEyeSlash /> : <FaEye />}
          </button>
        </div>

        <p className="text-xs text-gray-500 -mt-1">
          Dica: use letras e números para uma senha mais forte.
        </p>

        {/* confirmação */}
        <label className="text-sm font-semibold text-gray-600">Confirmar nova senha</label>
        <div className="relative">
          <input
            type={showConf ? "text" : "password"}
            value={confirmacao}
            onChange={(e) => setConfirmacao(e.target.value)}
            className={inputClass}
            autoComplete="new-password"
            placeholder="Repita a nova senha"
          />
          <button
            type="button"
            onClick={() => setShowConf((p) => !p)}
            className={toggleBtnClass}
            aria-label={showConf ? "Ocultar confirmação da senha" : "Mostrar confirmação da senha"}
            title={showConf ? "Ocultar" : "Mostrar"}
          >
            {showConf ? <FaEyeSlash /> : <FaEye />}
          </button>
        </div>

        <div className="mt-2 flex gap-2">
          <button
            type="submit"
            disabled={loading}
            className={`px-4 py-3 rounded-lg font-bold text-white shadow transition w-full sm:w-auto
              ${loading
                ? "bg-gray-400 cursor-not-allowed"
                : "!bg-gestor-blue hover:bg-gestor-blue/90 focus:ring-2 focus:ring-gestor-blue/20 border-0"}`}
          >
            {loading ? "Salvando..." : "Salvar nova senha"}
          </button>
        </div>
      </form>

      <style>{`
        .animate-fade-in { animation: fadeIn 0.5s cubic-bezier(.39,.575,.565,1) both; }
        @keyframes fadeIn { 0%{opacity:0;transform:translateY(12px)} 100%{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
};

export default TrocarSenha;
